import React, { useState, useEffect, useRef } from 'react';
import { Inbox, Plus, Send, X, AlertCircle, CheckCircle, Clock, MessageSquare, User, Shield, Calendar, Hash, Info } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './InboxSystem.css';

const InboxSystem = ({ user, socket, t }) => {
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'case', 'newReport'
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // New report form
  const [reportForm, setReportForm] = useState({
    subject: '',
    description: ''
  });
  
  // Message form
  const [messageForm, setMessageForm] = useState({
    message: ''
  });
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadCases();
    loadUnreadCount();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('newInboxCase', handleNewCase);
      socket.on('inboxMessageReceived', handleMessageReceived);
      socket.on('inboxCaseStatusChanged', handleCaseStatusChanged);
      socket.on('inboxCaseDeleted', handleCaseDeleted);
      
      return () => {
        socket.off('newInboxCase', handleNewCase);
        socket.off('inboxMessageReceived', handleMessageReceived);
        socket.off('inboxCaseStatusChanged', handleCaseStatusChanged);
        socket.off('inboxCaseDeleted', handleCaseDeleted);
      };
    }
  }, [socket]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedCase?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleNewCase = (data) => {
    toast.success(`${t('newMessage')} ${t('from')} ${data.from}`);
    loadCases();
    loadUnreadCount();
  };

  const handleMessageReceived = (data) => {
    toast.success(`${t('newMessage')} ${t('in')} ${t('caseNumber')} ${data.caseNumber}`);
    loadCases();
    loadUnreadCount();
    
    // If currently viewing this case, reload it
    if (selectedCase && selectedCase._id === data.caseId) {
      loadCase(data.caseId);
    }
  };

  const handleCaseStatusChanged = (data) => {
    const statusText = data.status === 'closed' ? t('closed') : t('reopened');
    toast.info(`${t('caseNumber')} ${data.caseNumber} ${t('hasBeenLabel')} ${statusText}`);
    loadCases();
    
    // If currently viewing this case, reload it
    if (selectedCase && selectedCase._id === data.caseId) {
      loadCase(data.caseId);
    }
  };

  const handleCaseDeleted = (data) => {
    toast.error(`${t('yourCase')} "${data.subject}" (${data.caseNumber}) ${t('hasBeenDeleted')} ${data.deletedBy}. ${t('reason')}: ${data.reason}`);
    
    // If currently viewing the deleted case, go back to list
    if (selectedCase && selectedCase.caseNumber === data.caseNumber) {
      setCurrentView('list');
      setSelectedCase(null);
    }
    
    loadCases();
    loadUnreadCount();
  };

  const loadCases = async () => {
    try {
      const response = await axios.get('/api/inbox/my-cases');
      setCases(response.data.cases);
    } catch (error) {
      console.error('Load cases error:', error);
      toast.error(t('failedToLoadInbox'));
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get('/api/inbox/unread-count');
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Load unread count error:', error);
    }
  };

  const loadCase = async (caseId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/inbox/cases/${caseId}`);
      setSelectedCase(response.data);
      setCurrentView('case');
    } catch (error) {
      console.error('Load case error:', error);
      toast.error(t('failedToLoadCase'));
    } finally {
      setLoading(false);
    }
  };

  const submitReport = async () => {
    if (!reportForm.subject.trim() || !reportForm.description.trim()) {
      toast.error(t('pleaseFillBothFields'));
      return;
    }

    if (reportForm.subject.trim().length < 5) {
      toast.error(t('subjectMinLength'));
      return;
    }

    if (reportForm.description.trim().length < 10) {
      toast.error(t('descriptionMinLength'));
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/inbox/report', reportForm);
      
      toast.success(t('reportSubmittedSuccessfully'));
      setReportForm({ subject: '', description: '' });
      setCurrentView('list');
      loadCases();
      loadUnreadCount();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToSubmitReport');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageForm.message.trim()) {
      toast.error(t('pleaseEnterMessage'));
      return;
    }

    if (messageForm.message.trim().length > 1000) {
      toast.error(t('messageTooLong'));
      return;
    }

    try {
      setLoading(true);
      await axios.post(`/api/inbox/cases/${selectedCase._id}/message`, {
        message: messageForm.message.trim()
      });
      
      setMessageForm({ message: '' });
      loadCase(selectedCase._id);
      loadCases();
      loadUnreadCount();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToSendMessage');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getCaseStatusBadge = (status) => {
    if (status === 'open') {
      return (
        <span className="status-badge open">
          <CheckCircle className="w-3 h-3" />
          {t('open')}
        </span>
      );
    } else {
      return (
        <span className="status-badge closed">
          <X className="w-3 h-3" />
          {t('closed')}
        </span>
      );
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}${t('mins')} ${t('ago')}`;
    } else if (diffHours < 24) {
      return `${diffHours}${t('hrs')} ${t('ago')}`;
    } else if (diffDays < 7) {
      return `${diffDays}${t('daysShort')} ${t('ago')}`;
    } else {
      return then.toLocaleDateString();
    }
  };

  const isUnreadCase = (caseData) => {
    if (!caseData.messages || caseData.messages.length === 0) return false;
    
    const userMessages = caseData.messages.filter(msg => 
      msg.from._id === user._id
    );
    const lastUserMessage = userMessages[userMessages.length - 1];
    const lastMessage = caseData.messages[caseData.messages.length - 1];
    
    if (lastMessage && lastMessage.from._id !== user._id) {
      if (!lastUserMessage || new Date(lastMessage.timestamp) > new Date(lastUserMessage.timestamp)) {
        return true;
      }
    }
    
    return false;
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      'main_admin': t('mainAdmin'),
      'supervisor': t('supervisor'), 
      'approver': t('approver'),
      'player': t('you'),
      'system': t('system')
    };
    return roleMap[role] || role;
  };

  const getRoleIcon = (role) => {
    if (['main_admin', 'manager', 'supervisor', 'approver'].includes(role)) {
      return <Shield className="w-4 h-4" />;
    } else if (role === 'system') {
      return <AlertCircle className="w-4 h-4" />;
    }
    return <User className="w-4 h-4" />;
  };

  if (currentView === 'newReport') {
    return (
      <div className="form-container">
        <div className="form-wrapper">
          {/* Header */}
          <div className="form-header">
            <div className="form-header-content">
              <h3 className="form-title">
                <Plus className="w-6 h-6" />
                {t('submitNewSupportReport')}
              </h3>
              <button
                onClick={() => setCurrentView('list')}
                className="close-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <div className="form-content">
            {/* Info Banner */}
            <div className="info-banner">
              <div className="info-banner-content">
                <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="info-banner-text">
                  <p className="info-banner-title">{t('beforeSubmitting')}</p>
                  <ul className="info-list">
                    <li>{t('beAsDetailed')}</li>
                    <li>{t('includeRelevantUsernames')}</li>
                    <li>{t('supportTeamWillReview')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Subject Field */}
            <div className="form-field">
              <label className="form-label">
                {t('subject')} <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                value={reportForm.subject}
                onChange={(e) => setReportForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={t('briefDescriptionPlaceholder')}
                className="form-input"
                maxLength={200}
                disabled={loading}
              />
              <div className="character-count">
                <p className="min-chars">
                  {t('minimumCharactersRequired')}
                </p>
                <p className="char-counter">
                  {reportForm.subject.length}/200
                </p>
              </div>
            </div>

            {/* Description Field */}
            <div className="form-field">
              <label className="form-label">
                {t('description')} <span className="required-asterisk">*</span>
              </label>
              <textarea
                value={reportForm.description}
                onChange={(e) => setReportForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('detailedDescriptionPlaceholder')}
                className="form-textarea"
                rows={8}
                maxLength={1000}
                disabled={loading}
              />
              <div className="character-count">
                <p className="min-chars">
                  {t('minimum10CharactersRequired')}
                </p>
                <p className="char-counter">
                  {reportForm.description.length}/1000
                </p>
              </div>
            </div>

            {/* Status Notice */}
            <div className="status-notice">
              <div className="status-notice-content">
                <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div className="status-notice-text">
                  <p className="status-notice-title">{t('submissionProcess')}</p>
                  <p className="status-notice-description">
                    {t('reportWillBeSubmitted')}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                onClick={submitReport}
                disabled={loading || !reportForm.subject.trim() || !reportForm.description.trim() || reportForm.subject.trim().length < 5 || reportForm.description.trim().length < 10}
                className="submit-button"
              >
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    {t('submitting')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('submitReport')}
                  </>
                )}
              </button>
              <button
                onClick={() => setCurrentView('list')}
                disabled={loading}
                className="cancel-button"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'case' && selectedCase) {
    return (
      <div className="chat-container">
        <div className="chat-wrapper">
          {/* Case Header */}
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-title-section">
                <div className="chat-title-row">
                  <MessageSquare className="w-6 h-6 text-orange-400" />
                  <h3 className="chat-title">{selectedCase.subject}</h3>
                  {getCaseStatusBadge(selectedCase.status)}
                </div>
                
                <div className="chat-info">
                  <div className="chat-info-item">
                    <Hash className="w-4 h-4 text-slate-400" />
                    <span>{t('caseNumber')} {selectedCase.caseNumber}</span>
                  </div>
                  <div className="chat-info-item">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span>{t('created')} {formatDate(selectedCase.createdAt)}</span>
                  </div>
                  <div className="chat-info-item">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>{t('updated')} {formatRelativeTime(selectedCase.lastActivity)}</span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  setCurrentView('list');
                  setSelectedCase(null);
                }}
                className="close-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages Container */}
          <div className="messages-section">
            {/* Messages */}
            <div className="messages-container">
              {selectedCase.messages.map((msg, index) => {
                const isFromUser = msg.from._id === user._id;
                const isSystem = msg.isSystemMessage;
                
                return (
                  <div
                    key={index}
                    className={`message ${isFromUser ? 'user' : isSystem ? 'system' : 'other'}`}
                  >
                    <div className="message-content">
                      {/* Message Header */}
                      <div className="message-header">
                        <div className={`sender-role ${msg.fromRole}`}>
                          {getRoleIcon(msg.fromRole)}
                          <span>
                            {isSystem ? t('system') : getRoleDisplay(msg.fromRole)}
                          </span>
                        </div>
                        <span className="message-time">
                          {formatRelativeTime(msg.timestamp)}
                        </span>
                      </div>
                      
                      {/* Message Content */}
                      <div className="message-bubble">
                        <p className="message-text">
                          {msg.message}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="message-input-section">
              {selectedCase.status === 'closed' ? (
                <div className="chat-closed-notice">
                  <div className="chat-closed-content">
                    <X className="w-5 h-5" />
                    <p className="chat-closed-text">
                      {t('thisCaseIsClosed')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="message-input-form">
                  <div className="message-input-row">
                    <textarea
                      value={messageForm.message}
                      onChange={(e) => setMessageForm({ message: e.target.value })}
                      placeholder={t('typeYourMessage')}
                      className="message-input"
                      rows={3}
                      maxLength={1000}
                      disabled={loading}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={loading || !messageForm.message.trim()}
                      className="send-message-button"
                    >
                      {loading ? (
                        <div className="loading-spinner"></div>
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      {loading ? t('sending') : t('send')}
                    </button>
                  </div>
                  <div className="message-input-footer">
                    <span className="input-hint">{t('pressEnterShift')}</span>
                    <span className="char-counter">{messageForm.message.length}/1000</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cases List View
  return (
    <div className="inbox-container">
      <div className="inbox-wrapper">
        {/* Header */}
        <div className="inbox-header">
          <div className="inbox-header-content">
            <div className="inbox-title-section">
              <Inbox className="w-6 h-6 text-white" />
              <h3 className="inbox-title">{t('supportInbox')}</h3>
              {unreadCount > 0 && (
                <span className="unread-badge">
                  {unreadCount} {t('newMessage')}
                </span>
              )}
            </div>
            <button
              onClick={() => setCurrentView('newReport')}
              className="new-report-button"
            >
              <Plus className="w-4 h-4" />
              {t('newReport')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="inbox-content">
          {cases.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Inbox className="w-12 h-12 text-slate-400" />
              </div>
              <h4 className="empty-state-title">{t('noCasesYet')}</h4>
              <p className="empty-state-description">
                {t('createNewReport')}
              </p>
              <button
                onClick={() => setCurrentView('newReport')}
                className="empty-state-button"
              >
                <Plus className="w-4 h-4" />
                {t('submitFirstReport')}
              </button>
            </div>
          ) : (
            <div className="cases-list">
              {cases.map((caseData) => {
                const unread = isUnreadCase(caseData);
                const lastMessage = caseData.messages?.[caseData.messages.length - 1];
                
                return (
                  <div
                    key={caseData._id}
                    onClick={() => loadCase(caseData._id)}
                    className={`case-item ${caseData.status} ${unread ? 'unread' : ''}`}
                  >
                    {/* Unread Indicator */}
                    {unread && (
                      <div className="new-message-badge">
                        {t('newMessage')}
                      </div>
                    )}

                    {/* Main Content */}
                    <div className="case-content">
                      {/* Header Row */}
                      <div className="case-header">
                        <div className="case-title-section">
                          <div className="case-title-row">
                            <h4 className="case-title">
                              {caseData.subject}
                            </h4>
                            {getCaseStatusBadge(caseData.status)}
                          </div>
                          
                          {/* Case Info */}
                          <div className="case-info">
                            <div className="case-info-item">
                              <Hash className="w-3 h-3" />
                              <span>{t('caseNumber')} {caseData.caseNumber}</span>
                            </div>
                            <div className="case-info-item">
                              <Calendar className="w-3 h-3" />
                              <span>{t('created')} {formatRelativeTime(caseData.createdAt)}</span>
                            </div>
                            <div className="case-info-item">
                              <MessageSquare className="w-3 h-3" />
                              <span>{caseData.messages?.length || 0} {t('messages')}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Last Message Preview */}
                      {lastMessage && (
                        <div className="last-message-preview">
                          <div className="message-sender-info">
                            <div className={`role-badge ${lastMessage.fromRole}`}>
                              {getRoleIcon(lastMessage.fromRole)}
                              <span>{getRoleDisplay(lastMessage.fromRole)}</span>
                            </div>
                            <span className="message-timestamp">
                              {formatRelativeTime(lastMessage.timestamp)}
                            </span>
                          </div>
                          <p className="message-preview">
                            {lastMessage.message}
                          </p>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="case-footer">
                        <span className="last-updated">
                          {t('lastUpdated')} {formatRelativeTime(caseData.lastActivity)}
                        </span>
                        
                        <div className="view-conversation">
                          <span>{t('clickToView')}</span>
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InboxSystem;